import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SeederNewJobModal } from "@/components/admin/seeder-new-job-modal";
import { SeederForeplayJobModal } from "@/components/admin/seeder-foreplay-job-modal";
import { SeederBrandApprovalGrid } from "@/components/admin/seeder-brand-approval-grid";
import { SeederScanningProgress } from "@/components/admin/seeder-scanning-progress";
import { SeederAdReviewGrid } from "@/components/admin/seeder-ad-review-grid";

interface SeedJob {
  id: number;
  niches: string[];
  status: string;
  adsPerBrand: number;
  sourceType?: string;
  creditCeiling?: number | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

interface SeedBrandCandidate {
  id: number;
  seedJobId: number;
  niche: string;
  brandName: string;
  metaPageId: string | null;
  metaPageName: string | null;
  adCount: number | null;
  verified: boolean;
  approved: boolean;
  scanStatus: string;
  adsScraped: number | null;
  scanError: string | null;
}

interface SeedJobAd {
  id: number;
  seedCandidateId: number | null;
  sourceBrand: string | null;
  imageUrl: string;
  adStyle: string | null;
  adAngle: string | null;
  daysLive: number | null;
  analysisStatus: string;
  promotedToLibrary: boolean;
  rejected: boolean;
  promptRecipe: string | null;
  colorPalette: string[] | null;
  composition: string | null;
}

interface SeedJobDetail {
  job: SeedJob;
  candidates: SeedBrandCandidate[];
  ads: SeedJobAd[];
}

function statusBadgeColor(status: string) {
  switch (status) {
    case "discovering":
    case "scanning":
      return "bg-blue-100 text-blue-800";
    case "awaiting_brand_approval":
    case "awaiting_review":
      return "bg-amber-100 text-amber-800";
    case "completed":
      return "bg-green-100 text-green-800";
    case "failed":
    case "cancelled":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

function statusLabel(status: string) {
  return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

export default function AdLibrarySeederPage() {
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [showForeplayModal, setShowForeplayModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const { data: jobs = [], isLoading: jobsLoading } = useQuery<SeedJob[]>({
    queryKey: ["/api/admin/seed-jobs"],
    queryFn: async () => {
      const res = await fetch("/api/admin/seed-jobs");
      if (!res.ok) throw new Error("Failed to load jobs");
      return res.json();
    },
    refetchInterval: 5000,
  });

  const shouldPollDetail = (status?: string) =>
    status === "discovering" || status === "scanning";

  const { data: detail } = useQuery<SeedJobDetail>({
    queryKey: [`/api/admin/seed-jobs/${selectedJobId}`],
    enabled: selectedJobId != null,
    queryFn: async () => {
      const res = await fetch(`/api/admin/seed-jobs/${selectedJobId}`);
      if (!res.ok) throw new Error("Failed to load job");
      return res.json();
    },
    refetchInterval: (query) => {
      const data = query.state.data as SeedJobDetail | undefined;
      return shouldPollDetail(data?.job?.status) ? 2000 : false;
    },
  });

  const createJob = useMutation({
    mutationFn: async (input: { niches: string[]; adsPerBrand: number }) => {
      const res = await fetch("/api/admin/seed-jobs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error((await res.json()).message || "Failed");
      return res.json() as Promise<SeedJob>;
    },
    onSuccess: (job) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/seed-jobs"] });
      setSelectedJobId(job.id);
      setShowNewJobModal(false);
    },
  });

  return (
    <div className="h-screen flex bg-white">
      {/* Left rail — job list */}
      <div className="w-80 border-r border-gray-200 overflow-y-auto">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Ad Library Seeder</h2>
          <div className="flex gap-1.5">
            <Button
              size="sm"
              onClick={() => setShowForeplayModal(true)}
              data-testid="button-new-foreplay-job"
            >
              <Plus className="h-4 w-4 mr-1" /> Find Brands
            </Button>
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {jobsLoading ? (
            <div className="p-4 flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading jobs...
            </div>
          ) : jobs.length === 0 ? (
            <div className="p-4 text-sm text-gray-500">
              No seed jobs yet. Click "New" to start one.
            </div>
          ) : (
            jobs.map((j) => (
              <button
                key={j.id}
                data-testid={`button-seed-job-${j.id}`}
                onClick={() => setSelectedJobId(j.id)}
                className={`w-full text-left p-3 hover:bg-gray-50 ${selectedJobId === j.id ? "bg-gray-100" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">#{j.id}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${statusBadgeColor(j.status)}`}>
                    {statusLabel(j.status)}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1 truncate">
                  {j.niches.join(", ")}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {new Date(j.createdAt).toLocaleString()}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Right panel — job detail */}
      <div className="flex-1 overflow-y-auto">
        {!selectedJobId ? (
          <div className="h-full flex items-center justify-center text-gray-400 text-sm">
            Select a job or create a new one.
          </div>
        ) : !detail ? (
          <div className="h-full flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : (
          <JobDetailPanel detail={detail} />
        )}
      </div>

      {showNewJobModal && (
        <SeederNewJobModal
          onClose={() => setShowNewJobModal(false)}
          onSubmit={(niches, adsPerBrand) => createJob.mutate({ niches, adsPerBrand })}
          isSubmitting={createJob.isPending}
        />
      )}

      {showForeplayModal && (
        <SeederForeplayJobModal
          onClose={() => setShowForeplayModal(false)}
          onCreated={(jobId) => {
            queryClient.invalidateQueries({ queryKey: ["/api/admin/seed-jobs"] });
            setSelectedJobId(jobId);
            setShowForeplayModal(false);
          }}
        />
      )}
    </div>
  );
}

function JobDetailPanel({ detail }: { detail: SeedJobDetail }) {
  const { job, candidates, ads } = detail;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold">
            Seed Job #{job.id}
            <span className={`ml-3 text-xs px-2 py-0.5 rounded ${statusBadgeColor(job.status)}`}>
              {statusLabel(job.status)}
            </span>
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Niches: {job.niches.join(", ")} · {job.adsPerBrand} ads per brand
          </p>
        </div>
      </div>

      {job.error && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm p-3 rounded mb-4">
          <strong>Error:</strong> {job.error}
        </div>
      )}

      {job.status === "discovering" && (
        <div className="flex items-center gap-2 text-sm text-gray-600 py-8">
          <Loader2 className="h-4 w-4 animate-spin" />
          Discovering brands from {job.niches.length} niche{job.niches.length === 1 ? "" : "s"}...
          <span className="text-xs text-gray-400 ml-2">({candidates.length} candidates so far)</span>
        </div>
      )}

      {job.status === "awaiting_brand_approval" && (
        <SeederBrandApprovalGrid jobId={job.id} candidates={candidates} />
      )}

      {job.status === "scanning" && (
        <SeederScanningProgress candidates={candidates} ads={ads} />
      )}

      {(job.status === "awaiting_review" || job.status === "completed") && (
        <>
          <div className="mb-4 grid grid-cols-4 gap-3 text-sm">
            <div className="p-3 rounded border border-gray-200">
              <div className="text-2xl font-semibold">{ads.length}</div>
              <div className="text-xs text-gray-500 mt-0.5">scraped</div>
            </div>
            <div className="p-3 rounded border border-gray-200">
              <div className="text-2xl font-semibold">{ads.filter((a) => a.analysisStatus === "complete").length}</div>
              <div className="text-xs text-gray-500 mt-0.5">analyzed</div>
            </div>
            <div className="p-3 rounded border border-green-200 bg-green-50">
              <div className="text-2xl font-semibold text-green-700">{ads.filter((a) => a.promotedToLibrary && !a.rejected).length}</div>
              <div className="text-xs text-green-700 mt-0.5">in library</div>
            </div>
            <div className="p-3 rounded border border-gray-200">
              <div className="text-2xl font-semibold text-gray-500">{ads.filter((a) => a.rejected).length}</div>
              <div className="text-xs text-gray-500 mt-0.5">rejected</div>
            </div>
          </div>
          {job.sourceType === "foreplay" && candidates.some((c) => c.scanStatus === "completed" && (c.adsScraped ?? 0) === 0) && (
            <div className="bg-amber-50 border border-amber-300 text-amber-900 text-xs p-3 rounded mb-4">
              <strong>0 ads came back for: {candidates.filter((c) => c.scanStatus === "completed" && (c.adsScraped ?? 0) === 0).map((c) => c.brandName).join(", ")}.</strong>{" "}
              Usually that means the directory entry is a thin duplicate page or the filters are too strict — re-add the brand via the exact-domain <em>Lookup</em> in the Foreplay modal (domains resolve to the flagship page), or lower "Min days running".
            </div>
          )}
          {job.sourceType === "foreplay" ? (
            <p className="text-xs text-gray-500 mb-4">
              Foreplay harvest — click ads to select your keepers, then <strong>Analyze</strong> submits them into Winning Ads. The rest stay here, so come back anytime to pick more.
            </p>
          ) : (
            <p className="text-xs text-gray-500 mb-4">
              Ads that pass vision analysis are auto-added to the library. Prune anything junky from the Winning Ads page.
            </p>
          )}
          <SeederAdReviewGrid jobId={job.id} ads={ads} candidates={candidates} />
        </>
      )}

      {(job.status === "failed" || job.status === "cancelled") && (
        <div className="text-sm text-gray-600">
          Job ended in status: <strong>{statusLabel(job.status)}</strong>.
        </div>
      )}
    </div>
  );
}
